// services/captcha/withCaptcha.ts
//
// Wraps the raw fetch so a captcha-protected (/api/v2) request answers its
// caller only once the captcha is done:
//
//   428 CAPTCHA_REQUIRED -> solve, retry the same request with the token
//   400 CAPTCHA_INVALID  -> solve again (the token is spent), retry once more
//   anything else        -> returned as is (503 CAPTCHA_UNAVAILABLE included:
//                           its `message` is toasted and the user tries later)
//
// At most two solves per request; after that the last CAPTCHA_INVALID goes
// back to the caller like any error.

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { requestCaptchaToken } from './broker';
import { CaptchaChallenge, CaptchaError, CaptchaErrorCode } from './types';

export const CAPTCHA_HEADER = 'X-Captcha-Token';
const MAX_SOLVES = 2;

type GatewayBody = {
  error?: { code?: string };
  data?: { captcha?: CaptchaChallenge };
};

/** The gateway's captcha code, if this error is one. Branch on it, not on the status. */
export const captchaErrorCode = (
  error: FetchBaseQueryError | undefined
): CaptchaErrorCode | undefined => {
  const code = (error?.data as GatewayBody | undefined)?.error?.code;
  return code === 'CAPTCHA_REQUIRED' ||
    code === 'CAPTCHA_INVALID' ||
    code === 'CAPTCHA_UNAVAILABLE'
    ? code
    : undefined;
};

const withToken = (args: string | FetchArgs, token: string): FetchArgs => {
  const request = typeof args === 'string' ? { url: args } : args;
  const headers = new Headers(request.headers as HeadersInit | undefined);
  headers.set(CAPTCHA_HEADER, token);
  return { ...request, headers };
};

export const withCaptcha =
  <Meta>(
    baseQuery: BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      {},
      Meta
    >
  ): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, Meta> =>
  async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    for (let solves = 0; solves < MAX_SOLVES; solves += 1) {
      const code = captchaErrorCode(result.error);
      if (code !== 'CAPTCHA_REQUIRED' && code !== 'CAPTCHA_INVALID') break;

      // Re-read on every answer: the gateway can switch provider or key.
      const challenge = (result.error!.data as GatewayBody).data?.captcha;
      if (!challenge?.provider || !challenge.site_key) break;

      let token: string;
      try {
        // Sequential on purpose: each retry depends on the answer before it.
        // eslint-disable-next-line no-await-in-loop
        token = await requestCaptchaToken(challenge);
      } catch (e) {
        // A dismissed challenge is the user's choice, not an error to toast.
        // Any other failure keeps the gateway's `message` as the toast.
        if (e instanceof CaptchaError && e.reason === 'cancelled') {
          (result.error as any).customHandledSnackbar = true;
        }
        break;
      }

      // eslint-disable-next-line no-await-in-loop
      result = await baseQuery(withToken(args, token), api, extraOptions);
    }

    return result;
  };
